<div class="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 lg:p-8">
	<!-- Header Section -->
	<div class="flex flex-col gap-4 mb-6 md:mb-8">
		<div>
			<h1 class="text-xl md:text-3xl font-bold text-[#5d202d] mb-1 md:mb-2">Staff Leaves</h1>
			<p class="text-sm md:text-base text-gray-600">Manage staff leave schedules and availability</p>
		</div>
		<div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
			<!-- Staff Filter -->
			<select
				[ngModel]="selectedStaffFilter()"
				(ngModelChange)="onStaffFilterChange($event)"
				class="px-3 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] bg-white flex-1 sm:flex-none">
				<option [ngValue]="null">All Staff</option>
				@for (staff of staffList(); track staff.id) {
				<option [ngValue]="staff.id">{{ staff.full_name }}</option>
				}
			</select>

			<button
				(click)="openModal()"
				class="px-4 py-2 md:px-6 bg-[#5d202d] text-white rounded-lg font-semibold hover:bg-[#5d202d]/90 transition-colors flex items-center justify-center gap-2 text-sm md:text-base">
				<svg class="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
				</svg>
				Add Leave
			</button>
		</div>
	</div>

	@if (isLoading()) {
	<!-- Loading State -->
	<div class="bg-white rounded-xl p-8 animate-pulse">
		<div class="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
		<div class="grid grid-cols-7 gap-2">
			@for (i of [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21]; track i) {
			<div class="h-24 bg-gray-100 rounded"></div>
			}
		</div>
	</div>
	} @else {
	<!-- Calendar View -->
	<div class="bg-white rounded-xl shadow-md overflow-hidden">
		<!-- Calendar Header -->
		<div class="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
			<button (click)="prevMonth()" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
				<svg class="w-4 h-4 md:w-5 md:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
				</svg>
			</button>
			<h2 class="text-base md:text-xl font-bold text-gray-800">{{ currentMonthLabel() }}</h2>
			<button (click)="nextMonth()" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
				<svg class="w-4 h-4 md:w-5 md:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
				</svg>
			</button>
		</div>

		<!-- Scrollable Calendar Container -->
		<div class="overflow-x-auto">
			<div class="min-w-[500px]">
				<!-- Day Headers -->
				<div class="grid grid-cols-7 border-b border-gray-200">
					@for (day of ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; track day) {
					<div class="px-2 md:px-4 py-2 md:py-3 text-center text-xs md:text-sm font-semibold text-gray-500 bg-gray-50">
						{{ day }}
					</div>
					}
				</div>

				<!-- Calendar Grid -->
				<div class="grid grid-cols-7">
					@for (day of calendarDays(); track day.date) {
					<div
						(click)="openModalForDate(day.date)"
						[class]="'min-h-[70px] md:min-h-[100px] p-1 md:p-2 border-b border-r border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ' +
							(!day.isCurrentMonth ? 'bg-gray-50/50 ' : '') +
							(day.isToday ? 'bg-blue-50/50 ' : '') +
							(day.isPast ? 'opacity-60 cursor-not-allowed ' : '')">
						<!-- Day Number -->
						<div class="flex items-center justify-between mb-0.5 md:mb-1">
							<span
								[class]="'text-xs md:text-sm font-medium ' +
									(day.isCurrentMonth ? 'text-gray-800' : 'text-gray-400') +
									(day.isToday ? ' text-blue-600 font-bold' : '')">
								{{ day.dayNumber }}
							</span>
					@if (day.leaves.length > 0) {
					<span class="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">
						{{ day.leaves.length }}
					</span>
					}
				</div>

				<!-- Leaves on this day -->
				<div class="space-y-1">
					@for (leave of day.leaves.slice(0, 3); track leave.id) {
					<div
						(click)="$event.stopPropagation(); openModal(leave)"
						[class]="'text-xs px-2 py-1 rounded truncate ' + getLeaveTypeColor(leave.leave_type)"
						[title]="(leave.staff?.full_name || '') + ' - ' + getLeaveTypeLabel(leave.leave_type)">
						{{ getStaffFirstName(leave.staff?.full_name) }}
					</div>
					}
					@if (day.leaves.length > 3) {
					<div class="text-xs text-gray-500 px-2">
						+{{ day.leaves.length - 3 }} more
					</div>
					}
						</div>
					</div>
					}
				</div>
			</div>
		</div>
	</div>

	<!-- Legend -->
	<div class="flex flex-wrap items-center justify-center gap-3 md:gap-6 mt-4 text-xs md:text-sm">
		<div class="flex items-center gap-1.5 md:gap-2">
			<div class="w-3 h-3 md:w-4 md:h-4 rounded bg-red-100 border border-red-200"></div>
			<span class="text-gray-600">Full Day</span>
		</div>
		<div class="flex items-center gap-1.5 md:gap-2">
			<div class="w-3 h-3 md:w-4 md:h-4 rounded bg-orange-100 border border-orange-200"></div>
			<span class="text-gray-600">Morning</span>
		</div>
		<div class="flex items-center gap-1.5 md:gap-2">
			<div class="w-3 h-3 md:w-4 md:h-4 rounded bg-yellow-100 border border-yellow-200"></div>
			<span class="text-gray-600">Afternoon</span>
		</div>
	</div>
	}
</div>

<!-- Modal Template -->
<ng-template #leaveModalTemplate>
	<div class="bg-white rounded-2xl shadow-2xl w-full">
		<!-- Modal Header -->
		<div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
			<div class="flex items-center justify-between">
				<h3 class="text-xl font-bold text-[#5d202d]">
					{{ editingId() ? 'Edit Leave' : 'Add Leave' }}
				</h3>
				<button (click)="closeModal()" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
					<svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
		</div>

		<!-- Modal Body -->
		<div class="px-6 py-4">
			@if (errorMessage()) {
			<div class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
				{{ errorMessage() }}
			</div>
			}

			<form [formGroup]="leaveForm" (ngSubmit)="saveLeave()" class="space-y-4">
				<!-- Staff Selection -->
				<div>
					<label class="block text-sm font-semibold text-gray-700 mb-1">Staff Member *</label>
					<select
						formControlName="staff_id"
						class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all">
						<option value="">Select staff member</option>
						@for (staff of staffList(); track staff.id) {
						<option [value]="staff.id">{{ staff.full_name }} ({{ staff.role }})</option>
						}
					</select>
				</div>

				<!-- Multiple Days Toggle (only for new leaves) -->
				@if (!editingId()) {
				<div class="flex items-center gap-2">
					<input
						type="checkbox"
						id="multipleDays"
						[checked]="isMultipleDays()"
						(change)="toggleMultipleDays()"
						class="w-4 h-4 text-[#5d202d] border-gray-300 rounded focus:ring-[#5d202d]" />
					<label for="multipleDays" class="text-sm text-gray-700">Add multiple days</label>
				</div>
				}

				@if (!isMultipleDays()) {
				<!-- Single Date -->
				<div>
					<label class="block text-sm font-semibold text-gray-700 mb-1">Leave Date *</label>
					<input
						type="date"
						formControlName="leave_date"
						[min]="editingId() ? '' : todayDateString"
						class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all" />
				</div>
				} @else {
				<!-- Multiple Date Selection -->
				<div>
					<label class="block text-sm font-semibold text-gray-700 mb-2">
						Select Dates * ({{ selectedDates().length }} selected)
					</label>
					<div class="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 grid grid-cols-4 gap-2">
						@for (fd of futureDates; track fd.date) {
						<button
							type="button"
							(click)="toggleDateSelection(fd.date)"
							[class]="'px-2 py-1.5 text-xs rounded border transition-colors ' +
								(isDateSelected(fd.date) ? 'bg-[#5d202d] text-white border-[#5d202d]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#5d202d]')">
							{{ fd.label }}
						</button>
						}
					</div>
				</div>
				}

				<!-- Leave Type -->
				<div>
					<label class="block text-sm font-semibold text-gray-700 mb-1">Leave Type *</label>
					<select
						formControlName="leave_type"
						class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all">
						<option value="full_day">Full Day</option>
						<option value="half_day_morning">Half Day - Morning</option>
						<option value="half_day_afternoon">Half Day - Afternoon</option>
					</select>
				</div>

				<!-- Reason -->
				<div>
					<label class="block text-sm font-semibold text-gray-700 mb-1">Reason (Optional)</label>
					<textarea
						formControlName="reason"
						rows="2"
						placeholder="Enter reason for leave..."
						class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all resize-none"></textarea>
				</div>

				<!-- Actions -->
				<div class="flex items-center justify-between pt-4 border-t border-gray-200">
					@if (editingId()) {
					<button
						type="button"
						(click)="deleteCurrentLeave()"
						class="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors">
						Delete
					</button>
					} @else {
					<div></div>
					}

					<div class="flex items-center gap-3">
						<button
							type="button"
							(click)="closeModal()"
							class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors">
							Cancel
						</button>
						<button
							type="submit"
							[disabled]="isSubmitting() || leaveForm.invalid || (isMultipleDays() && selectedDates().length === 0)"
							class="px-6 py-2 bg-[#5d202d] text-white rounded-lg font-semibold hover:bg-[#5d202d]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
							@if (isSubmitting()) {
							<svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
								<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
								<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
							</svg>
							Saving...
							} @else {
							{{ editingId() ? 'Update' : (isMultipleDays() ? 'Add ' + selectedDates().length + ' Leave(s)' : 'Add Leave') }}
							}
						</button>
					</div>
				</div>
			</form>
		</div>
	</div>
</ng-template>
